<script>
import { Helper, debounce } from '@/mixin/helper'
import moment from 'moment'
import * as syncIccidLogApi from '@/services/rest-api-sms/lib/sync-iccid-log/sync-iccid-log'

export default {
  name: 'SyncIccidLog',

  props: {
    modal: {
      type: Boolean,
      required: true,
    },
  },

  mixins: [Helper],

  data() {
    return {
      isModalOpen: false,
      loading: false,
      filter: '',
      currentPage: 1,
      perPage: 10,
      totalPages: 0,
      totalRows: 0,
      pageOptions: [10, 25, 50, 100],
      filteredItems: [],
      fields: [
        { key: 'created_at', label: 'Date Time' },
        { key: 'total_data', label: 'Total Data' },
        { key: 'status', label: 'Status' },
        { key: 'filter', label: 'Filter' },
        { key: 'server_region', label: 'Server Region' },
        { key: 'retry', label: 'Retry' },
        { key: 'action', label: 'Action' },
        { key: 'created_by', label: 'Created By' },
      ],
    }
  },

  watch: {
    modal: {
      immediate: true,
      handler(newValue) {
        this.isModalOpen = newValue
        if (newValue) {
          this.fetchLogs()
        }
      },
    },
    isModalOpen(newValue) {
      this.$emit('update:modal', newValue)
    },
  },

  methods: {
    debounceFetchLogs: debounce(function () {
      this.currentPage = 1
      this.fetchLogs()
    }, 500),

    async fetchLogs() {
      this.loading = true
      const params = {
        page: this.currentPage,
        limit: this.perPage,
        search: this.filter || undefined,
      }

      try {
        const response = await syncIccidLogApi.get(params)

        if (!response?.data?.paginated_result) return

        this.filteredItems = response.data.paginated_result
        this.totalRows = response.metadata?.total_count || 0
        this.totalPages = response.metadata?.total_pages || 0
        this.currentPage = response.metadata?.page || this.currentPage
      } catch (error) {
        this.$toast.error('Error loading Sync ICCID logs')
      } finally {
        this.loading = false
      }
    },

    resetModal() {
      this.filter = ''
      this.currentPage = 1
      this.filteredItems = []
    },

    changePage(page) {
      this.currentPage = page
      this.fetchLogs()
    },

    resetFilter(newPerPage) {
      this.perPage = newPerPage
      this.currentPage = 1
      this.fetchLogs()
    },

    getStatusClass(status) {
      if (status === 'Success' || status === 'success') return 'text-success'
      if (status === 'Failed' || status === 'failed' || status === 'fail') return 'text-danger'
      if (status === 'waiting' || status === 'pending') return 'text-warning'
      if (status === 'inprogress' || status === 'In Progress') return 'text-primary'
      return 'text-muted'
    },

    formatFilter(value) {
      if (!value) return '-'

      try {
        const parsed = JSON.parse(value)
        if (Array.isArray(parsed.filters)) {
          return parsed.filters.join(', ')
        }
        return value
      } catch (error) {
        return value
      }
    },
  },

  mounted() {
    moment.locale('id')
  },
}
</script>

<template>
  <b-modal
    v-model="isModalOpen"
    id="sync-iccid-log-modal"
    title="Sync ICCID Log"
    size="xl"
    :hide-footer="true"
    @hidden="resetModal"
  >
    <div class="d-flex justify-content-between align-items-center mb-3">
      <b-form-input
        v-model="filter"
        class="w-50"
        size="sm"
        placeholder="Search"
        @input="debounceFetchLogs"
      />
    </div>

    <div v-if="loading" class="text-center py-3">
      <b-spinner class="align-middle" small></b-spinner>
      <p class="mt-2">Loading Sync ICCID Logs...</p>
    </div>

    <div v-else>
      <div class="table-responsive my-3 border border-1">
        <b-table
          class="datatables"
          :items="filteredItems"
          :fields="fields"
          :busy="loading"
          show-empty
          responsive="sm"
          hover
          :empty-text="$t('global.emptyTable')"
          :empty-filtered-text="$t('global.emptyFilteredTable')"
        >
          <template #cell(created_at)="data">
            {{ data.item.created_at ? momentDMHms(data.value) : '-' }}
          </template>

          <template #cell(total_data)="data">
            {{ data.value || '-' }}
          </template>

          <template #cell(status)="data">
            <span :class="getStatusClass(data.value)">
              {{ data.value || '-' }}
            </span>
          </template>

          <template #cell(filter)="data">
            {{ formatFilter(data.value) }}
          </template>

          <template #cell(server_region)="data">
            {{ data.value || '-' }}
          </template>

          <template #cell(retry)="data">
            {{ data.value !== null && data.value !== undefined ? data.value : '-' }}
          </template>

          <template #cell(action)="data">
            {{ data.value || '-' }}
          </template>

          <template #cell(created_by)="data">
            {{ data.value || '-' }}
          </template>
        </b-table>
      </div>

      <div class="row">
        <div class="col-sm-12 col-md-6">
          <div id="sync-iccid-table-length" class="dataTables_length">
            <label class="d-inline-flex align-items-center">
              {{ $t('show') }}
              <b-form-select
                class="form-select form-select-sm"
                v-model="perPage"
                size="sm"
                :options="pageOptions"
                @change="resetFilter"
              />
              {{ $t('entry') }}. {{ $t('show') }}&nbsp;<strong>{{
                filteredItems.length > 0 ? (currentPage - 1) * perPage + 1 : 0
              }}</strong>
              &nbsp;{{ $t('to') }}&nbsp;<strong>{{
                currentPage != totalPages
                  ? currentPage * perPage
                  : (currentPage - 1) * perPage + filteredItems.length
              }}</strong>
              &nbsp;{{ $t('from') }}&nbsp;<strong>{{ totalRows }}</strong
              >&nbsp;{{ $t('entry') }}.
            </label>
          </div>
        </div>
        <div class="col-sm-12 col-md-6">
          <div class="dataTables_paginate paging_simple_numbers float-end">
            <ul class="pagination pagination-rounded mb-0">
              <b-pagination
                v-model="currentPage"
                :total-rows="totalRows"
                :per-page="perPage"
                @change="changePage"
              />
            </ul>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>
